import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  type CSSProperties,
  type ReactNode,
} from "react";
import { CATEGORIES } from "../data/catalogue";
import type {
  CatalogueItem,
  Category,
  ParticipantAnswer,
  SharedNote,
} from "../types";
import { InlineAnswerSummary } from "./ResponseControls";
import { BookmarkIcon, ChevronDownIcon } from "./Icons";

export interface CatalogueItemSummary {
  ownAnswer: ParticipantAnswer | null;
  partnerAnswer: ParticipantAnswer | null;
  partnerState: "visible" | "saved" | "none";
  ownSaved: boolean;
  rowImportant: boolean;
  note: SharedNote | null;
}

export type CatalogueRailMode = "answer" | "follow" | "browse";

const ACTIVE_ROW_TRANSITION_STYLE = {
  viewTransitionName: "catalogue-active-row",
} as CSSProperties;

export function CatalogueRail({
  items,
  mode,
  activeItemId,
  transitionItemId,
  partnerActiveItemId,
  ownName,
  partnerName,
  itemSummaries,
  collapsedCategoryIds,
  activeContent,
  onSelect,
  onToggleRowImportant,
  onToggleCategory,
}: {
  items: CatalogueItem[];
  mode: CatalogueRailMode;
  activeItemId: string | null;
  transitionItemId: string | null;
  partnerActiveItemId: string | null;
  ownName: string;
  partnerName: string;
  itemSummaries: ReadonlyMap<string, CatalogueItemSummary>;
  collapsedCategoryIds: ReadonlySet<string>;
  activeContent: ReactNode | null;
  onSelect: (itemId: string) => void;
  onToggleRowImportant: (itemId: string) => Promise<void>;
  onToggleCategory: (categoryId: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const previousModeRef = useRef(mode);
  const lastActiveItemRef = useRef<string | null>(null);
  const pendingScrollItemRef = useRef<string | null>(activeItemId);
  const pendingScrollBehaviorRef = useRef<ScrollBehavior>("auto");
  const wheelAccumulatorRef = useRef(0);
  const wheelDirectionRef = useRef<-1 | 0 | 1>(0);
  const wheelTargetIndexRef = useRef(0);
  const lastWheelAtRef = useRef(0);
  const wheelResetTimerRef = useRef<number | null>(null);
  const itemIndexes = useMemo(
    () => new Map(items.map((item, index) => [item.id, index])),
    [items],
  );
  const activeIndex = activeItemId === null
    ? -1
    : (itemIndexes.get(activeItemId) ?? 0);
  const categories = useMemo(() => categoriesFor(items), [items]);

  useLayoutEffect(() => {
    const previousMode = previousModeRef.current;
    previousModeRef.current = mode;
    const crossesBrowseBoundary =
      previousMode !== mode &&
      (previousMode === "browse" || mode === "browse");
    const browseResultsChanged = previousMode === mode && mode === "browse";
    if (
      (!crossesBrowseBoundary && !browseResultsChanged) ||
      transitionItemId === null
    ) {
      return;
    }
    const container = containerRef.current;
    const element = container?.querySelector<HTMLElement>(
      `[data-item-id="${CSS.escape(transitionItemId)}"]`,
    );
    if (!container || !element) return;
    positionRailItem(container, element, "auto", true);
  }, [items, mode, transitionItemId]);

  useEffect(() => {
    if (activeIndex < 0) return;
    wheelTargetIndexRef.current = activeIndex;
  }, [activeIndex]);

  useEffect(() => {
    if (mode !== "answer" || activeIndex < 0) return;
    const container = containerRef.current;
    if (!container) return;
    const onDocumentWheel = (event: WheelEvent) => {
      if (event.ctrlKey || Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (
        !target ||
        target.closest(".modal") ||
        !target.closest(".board-shell")
      ) {
        return;
      }
      const textarea = target.closest("textarea");
      if (textarea && document.activeElement === textarea) {
        const scrollingDown = event.deltaY > 0;
        const canScroll = scrollingDown
          ? textarea.scrollTop + textarea.clientHeight <
            textarea.scrollHeight - 1
          : textarea.scrollTop > 0;
        if (canScroll) return;
      }
      event.preventDefault();
      const multiplier =
        event.deltaMode === WheelEvent.DOM_DELTA_LINE
          ? 24
          : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
            ? container.clientHeight
            : 1;
      const delta = event.deltaY * multiplier;
      const direction = Math.sign(delta) as -1 | 0 | 1;
      if (direction === 0) return;
      const now = performance.now();
      if (now - lastWheelAtRef.current > 220) {
        wheelAccumulatorRef.current = 0;
        wheelTargetIndexRef.current = activeIndex;
      }
      lastWheelAtRef.current = now;
      if (
        wheelDirectionRef.current !== 0 &&
        wheelDirectionRef.current !== direction
      ) {
        wheelAccumulatorRef.current = 0;
        wheelTargetIndexRef.current = activeIndex;
      }
      wheelDirectionRef.current = direction;
      wheelAccumulatorRef.current += delta;
      if (wheelResetTimerRef.current !== null) {
        window.clearTimeout(wheelResetTimerRef.current);
      }
      wheelResetTimerRef.current = window.setTimeout(() => {
        wheelAccumulatorRef.current = 0;
        wheelDirectionRef.current = 0;
      }, 180);

      const magnitude = Math.abs(wheelAccumulatorRef.current);
      if (magnitude < 52) return;
      const steps = Math.min(
        6,
        Math.max(1, Math.floor((magnitude / 52) ** 0.72)),
      );
      wheelAccumulatorRef.current = 0;
      const nextIndex = Math.max(
        0,
        Math.min(
          items.length - 1,
          wheelTargetIndexRef.current + direction * steps,
        ),
      );
      wheelTargetIndexRef.current = nextIndex;
      const nextItem = items[nextIndex];
      if (nextItem && nextIndex !== activeIndex) {
        onSelect(nextItem.id);
      }
    };
    document.addEventListener("wheel", onDocumentWheel, { passive: false });
    return () => {
      document.removeEventListener("wheel", onDocumentWheel);
      if (wheelResetTimerRef.current !== null) {
        window.clearTimeout(wheelResetTimerRef.current);
      }
    };
  }, [activeIndex, items, mode, onSelect]);

  useEffect(() => {
    if (activeItemId === null) {
      lastActiveItemRef.current = null;
      pendingScrollItemRef.current = null;
      return;
    }
    if (lastActiveItemRef.current !== activeItemId) {
      pendingScrollBehaviorRef.current =
        lastActiveItemRef.current === null ? "auto" : "smooth";
      lastActiveItemRef.current = activeItemId;
      pendingScrollItemRef.current = activeItemId;
    }
    if (pendingScrollItemRef.current !== activeItemId) return;
    const frame = requestAnimationFrame(() => {
      const container = containerRef.current;
      const element = container?.querySelector<HTMLElement>(
        `[data-active-item="${CSS.escape(activeItemId)}"]`,
      );
      if (
        !container ||
        !element ||
        element.hidden ||
        element.offsetParent === null
      ) {
        return;
      }
      positionRailItem(
        container,
        element,
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : pendingScrollBehaviorRef.current,
        window.matchMedia("(min-width: 1101px)").matches,
      );
      pendingScrollItemRef.current = null;
    });
    return () => cancelAnimationFrame(frame);
  }, [activeItemId, collapsedCategoryIds]);

  useEffect(() => {
    if (mode === "browse" || activeItemId === null) return;
    const container = containerRef.current;
    if (!container) return;
    let frame: number | null = null;
    const preserveActiveRowPosition = (event: FocusEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        !target?.closest(
          `[data-active-item="${CSS.escape(activeItemId)}"]`,
        ) ||
        !window.matchMedia("(min-width: 1101px)").matches
      ) {
        return;
      }
      if (frame !== null) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const element = container.querySelector<HTMLElement>(
          `[data-active-item="${CSS.escape(activeItemId)}"]`,
        );
        const categoryHeader = element
          ?.closest(".catalogue-category")
          ?.querySelector<HTMLElement>(".catalogue-category-toggle");
        if (
          element &&
          element.getBoundingClientRect().height <=
            container.clientHeight - (categoryHeader?.offsetHeight ?? 0)
        ) {
          positionRailItem(container, element, "auto", true);
        }
      });
    };
    container.addEventListener("focusin", preserveActiveRowPosition);
    container.addEventListener("focusout", preserveActiveRowPosition);
    return () => {
      container.removeEventListener("focusin", preserveActiveRowPosition);
      container.removeEventListener("focusout", preserveActiveRowPosition);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [activeItemId, mode]);

  useEffect(() => {
    if (mode !== "answer" || activeIndex < 0) return;
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest(".modal")) return;
      const isTextEditing =
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLInputElement ||
        target instanceof HTMLSelectElement ||
        Boolean(target?.isContentEditable);

      if (
        (event.key === "`" || event.code === "Backquote") &&
        !isTextEditing
      ) {
        event.preventDefault();
        focusResponseScale();
        return;
      }

      if (event.key === "PageDown" || event.key === "PageUp") {
        event.preventDefault();
        event.stopPropagation();
        const direction = event.key === "PageUp" ? -1 : 1;
        const item = items[
          Math.max(0, Math.min(items.length - 1, activeIndex + direction))
        ];
        if (item) onSelect(item.id);
        return;
      }

      if (
        event.key === "Tab" &&
        target instanceof HTMLTextAreaElement &&
        target.closest(".item-workspace")
      ) {
        event.preventDefault();
        focusRelativeControl(target, event.shiftKey ? -1 : 1);
        return;
      }
      if (isTextEditing || target?.closest(".choice-scale")) return;

      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        event.preventDefault();
        focusRelativeControl(
          document.activeElement as HTMLElement | null,
          event.key === "ArrowLeft" ? -1 : 1,
        );
        return;
      }
      if (
        ![
          "ArrowDown",
          "ArrowUp",
          "Home",
          "End",
        ].includes(event.key)
      ) {
        return;
      }
      event.preventDefault();
      let next = activeIndex;
      if (event.key === "ArrowDown") next += 1;
      if (event.key === "ArrowUp") next -= 1;
      if (event.key === "Home") next = 0;
      if (event.key === "End") next = items.length - 1;
      const item = items[Math.max(0, Math.min(items.length - 1, next))];
      if (item) onSelect(item.id);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [activeIndex, items, mode, onSelect]);

  return (
    <div
      className={`catalogue-stream catalogue-stream-${mode}`}
      ref={containerRef}
      tabIndex={0}
      aria-label="Catalogue"
    >
      {activeItemId !== null ? (
        <div className="catalogue-leading-space" aria-hidden="true" />
      ) : null}
      {categories.map((category) => {
        const categoryItems = items.filter(
          (item) => item.categoryId === category.id,
        );
        const collapsed =
          mode === "browse" ? false : collapsedCategoryIds.has(category.id);
        const completed = categoryItems.filter(
          (item) => itemSummaries.get(item.id)?.ownSaved,
        ).length;
        const containsActive =
          activeItemId !== null &&
          categoryItems.some((item) => item.id === activeItemId);
        const keepMounted = mode === "browse" || !collapsed || containsActive;

        return (
          <section
            className={`catalogue-category ${containsActive ? "current" : ""}`}
            key={category.id}
            data-category-id={category.id}
          >
            <button
              type="button"
              className="catalogue-category-toggle"
              aria-expanded={!collapsed}
              onClick={() => {
                const scrollTop = containerRef.current?.scrollTop;
                onToggleCategory(category.id);
                requestAnimationFrame(() => {
                  if (containerRef.current && scrollTop !== undefined) {
                    containerRef.current.scrollTop = scrollTop;
                  }
                });
              }}
            >
              <span>
                <small>{String(category.number).padStart(2, "0")}</small>
                <strong>{category.name}</strong>
              </span>
              <span className="category-meta">
                {containsActive && partnerActiveItemId === activeItemId ? (
                  <span className="partner-presence">
                    <i aria-hidden="true" />
                    {partnerName} is here
                  </span>
                ) : null}
                <span>
                  {completed} / {categoryItems.length} saved
                </span>
                <ChevronDownIcon className="category-chevron" />
              </span>
            </button>

            {keepMounted ? (
              <div className="catalogue-category-items" hidden={collapsed}>
                {categoryItems.map((item) => {
                  if (activeItemId !== null && item.id === activeItemId) {
                    return (
                      <div
                        className="catalogue-active-item"
                        data-active-item={item.id}
                        data-item-id={item.id}
                        key={item.id}
                        style={
                          item.id === transitionItemId
                            ? ACTIVE_ROW_TRANSITION_STYLE
                            : undefined
                        }
                      >
                        {activeContent}
                      </div>
                    );
                  }

                  const distance = activeIndex < 0
                    ? 0
                    : Math.abs(
                        (itemIndexes.get(item.id) ?? 0) - activeIndex,
                      );
                  const distanceClass = Math.min(distance, 5);
                  const summary = itemSummaries.get(item.id);
                  const hasSummary = hasCatalogueSummary(summary);
                  return (
                    <div
                      id={`catalogue-${item.id}`}
                      data-item-id={item.id}
                      key={item.id}
                      className={`catalogue-item-row distance-${distanceClass} ${summary?.rowImportant ? "row-important" : ""}`}
                      style={
                        item.id === transitionItemId
                          ? ACTIVE_ROW_TRANSITION_STYLE
                          : undefined
                      }
                    >
                      <button
                        type="button"
                        className="catalogue-item-main"
                        onClick={() => onSelect(item.id)}
                      >
                        <span className="catalogue-item-term">{item.term}</span>
                        {mode === "browse" ? (
                          <CatalogueBrowseDetails
                            item={item}
                            summary={summary}
                            ownName={ownName}
                            partnerName={partnerName}
                          />
                        ) : hasSummary && summary ? (
                          <CatalogueSummary
                            summary={summary}
                            ownName={ownName}
                            partnerName={partnerName}
                          />
                        ) : distance <= 1 ? (
                          <span className="catalogue-item-definition">
                            {item.definition}
                          </span>
                        ) : null}
                        {item.id === partnerActiveItemId ? (
                          <span className="partner-presence">
                            <i aria-hidden="true" />
                            {partnerName} is here
                          </span>
                        ) : null}
                      </button>
                      <button
                        type="button"
                        className="row-bookmark"
                        aria-label={
                          summary?.rowImportant
                            ? `Remove bookmark from ${item.term}`
                            : `Bookmark ${item.term}`
                        }
                        aria-pressed={Boolean(summary?.rowImportant)}
                        onClick={() => void onToggleRowImportant(item.id)}
                      >
                        <BookmarkIcon />
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </section>
        );
      })}
      {activeItemId !== null ? (
        <div className="catalogue-trailing-space" aria-hidden="true" />
      ) : null}
    </div>
  );
}

function positionRailItem(
  container: HTMLElement,
  element: HTMLElement,
  behavior: ScrollBehavior,
  centre: boolean,
) {
  if (element.hidden || element.offsetParent === null) return;
  const containerBounds = container.getBoundingClientRect();
  const elementBounds = element.getBoundingClientRect();
  const categoryHeader = element
    .closest(".catalogue-category")
    ?.querySelector<HTMLElement>(".catalogue-category-toggle");
  const headerHeight = categoryHeader?.offsetHeight ?? 0;
  const availableHeight = Math.max(0, container.clientHeight - headerHeight);
  const desiredTop =
    centre && elementBounds.height <= availableHeight
      ? headerHeight + (availableHeight - elementBounds.height) / 2
      : headerHeight;
  const target =
    container.scrollTop +
    elementBounds.top -
    containerBounds.top -
    desiredTop;
  container.scrollTo({ top: Math.max(0, target), behavior });
}

function categoriesFor(items: CatalogueItem[]): Category[] {
  const present = new Set(items.map((item) => item.categoryId));
  const canonical = CATEGORIES.filter((category) => present.has(category.id));
  const known = new Set(canonical.map((category) => category.id));
  const additional = items
    .filter((item) => !known.has(item.categoryId))
    .map((item, index) => ({
      id: item.categoryId,
      number: CATEGORIES.length + index + 1,
      name: item.categoryName,
    }))
    .filter(
      (category, index, all) =>
        all.findIndex((candidate) => candidate.id === category.id) === index,
    );
  return [...canonical, ...additional];
}

export function hasCatalogueSummary(
  summary: CatalogueItemSummary | undefined,
) {
  return Boolean(
    summary?.ownAnswer ||
      summary?.partnerAnswer ||
      summary?.partnerState === "saved",
  );
}

export function CatalogueSummary({
  summary,
  ownName,
  partnerName,
  includeImportantContext = true,
}: {
  summary: CatalogueItemSummary;
  ownName: string;
  partnerName: string;
  includeImportantContext?: boolean;
}) {
  return (
    <span className="catalogue-item-summary">
      {summary.ownAnswer ? (
        <InlineAnswerSummary
          answer={summary.ownAnswer}
          participant={ownName}
        />
      ) : null}
      {summary.partnerAnswer ? (
        <InlineAnswerSummary
          answer={summary.partnerAnswer}
          participant={partnerName}
        />
      ) : summary.partnerState === "saved" ? (
        <span className="inline-answer-pending">
          <strong>{partnerName}</strong>
          saved · hidden until you save
        </span>
      ) : null}
      {includeImportantContext
        ? importantContextSummary(summary, ownName, partnerName)
        : null}
    </span>
  );
}

function CatalogueBrowseDetails({
  item,
  summary,
  ownName,
  partnerName,
}: {
  item: CatalogueItem;
  summary: CatalogueItemSummary | undefined;
  ownName: string;
  partnerName: string;
}) {
  return (
    <span className="catalogue-browse-details">
      <span className="catalogue-item-definition">{item.definition}</span>
      {summary && hasCatalogueSummary(summary) ? (
        <CatalogueSummary
          summary={summary}
          ownName={ownName}
          partnerName={partnerName}
          includeImportantContext={false}
        />
      ) : null}
      {summary?.ownAnswer?.context.trim() ? (
        <BrowseDetail
          label={`${ownName} · context`}
          important={summary.ownAnswer.important}
        >
          {summary.ownAnswer.context}
        </BrowseDetail>
      ) : null}
      {summary?.note?.text.trim() ? (
        <BrowseDetail label="Shared note" important={summary.note.important}>
          {summary.note.text}
        </BrowseDetail>
      ) : null}
      {summary?.partnerAnswer?.context.trim() ? (
        <BrowseDetail
          label={`${partnerName} · context`}
          important={summary.partnerAnswer.important}
        >
          {summary.partnerAnswer.context}
        </BrowseDetail>
      ) : null}
    </span>
  );
}

function BrowseDetail({
  label,
  important = false,
  children,
}: {
  label: string;
  important?: boolean;
  children: ReactNode;
}) {
  return (
    <span className={`catalogue-browse-detail ${important ? "important" : ""}`}>
      <strong>{label}</strong>
      <span>{children}</span>
    </span>
  );
}

function importantContextSummary(
  summary: CatalogueItemSummary,
  ownName: string,
  partnerName: string,
) {
  const contexts = [
    [ownName, summary.ownAnswer],
    [partnerName, summary.partnerAnswer],
  ] as const;
  return contexts.map(([name, answer]) =>
    answer?.important && answer.context.trim() ? (
      <span className="important-context-summary" key={name}>
        <strong>{name}</strong>
        <span>{answer.context}</span>
      </span>
    ) : null,
  );
}

function focusRelativeControl(
  origin: HTMLElement | null,
  direction: -1 | 1,
) {
  const workspace =
    origin?.closest<HTMLElement>(".item-workspace") ??
    document.querySelector<HTMLElement>(".catalogue-active-item .item-workspace");
  if (!workspace) return;
  const controls = [
    ...workspace.querySelectorAll<HTMLElement>(
      "[data-flow-control]:not([disabled])",
    ),
  ].filter((element) => element.offsetParent !== null);
  if (!controls.length) return;
  const currentIndex = origin ? controls.indexOf(origin) : -1;
  const nextIndex =
    currentIndex < 0
      ? direction > 0
        ? 0
        : controls.length - 1
      : Math.max(0, Math.min(controls.length - 1, currentIndex + direction));
  controls[nextIndex]?.focus({ preventScroll: true });
}

function focusResponseScale() {
  const workspace = document.querySelector<HTMLElement>(
    ".catalogue-active-item .item-workspace",
  );
  if (!workspace) return;
  const scales = [
    ...workspace.querySelectorAll<HTMLElement>(
      ".own-response .choice-scale:not([disabled])",
    ),
  ].filter((element) => element.offsetParent !== null);
  const target =
    scales.find((scale) => scale.dataset.complete === "false") ?? scales[0];
  target?.focus({ preventScroll: true });
}
